import { datadogRum } from '@datadog/browser-rum';
import { reactPlugin } from '@datadog/browser-rum-react';
import {
  DATADOG_APPLICATION_ID,
  DATADOG_CLIENT_TOKEN,
  DATADOG_SITE,
  DATADOG_SERVICE,
  DATADOG_ENV,
  DATADOG_VERSION,
  DATADOG_ENABLED,
} from './constants';

/**
 * Initialize Datadog RUM (Real User Monitoring)
 * 
 * This function should be called once at application startup, before React renders.
 * It sets up browser monitoring for user sessions, errors, and performance metrics.
 */
export function initDatadog(): void {
  if (!DATADOG_ENABLED) {
    console.log('[Datadog] RUM monitoring disabled');
    return;
  }

  if (!DATADOG_APPLICATION_ID || !DATADOG_CLIENT_TOKEN) {
    console.warn('[Datadog] Missing APPLICATION_ID or CLIENT_TOKEN. RUM monitoring will not be initialized.');
    return;
  }

  datadogRum.init({
    applicationId: DATADOG_APPLICATION_ID,
    clientToken: DATADOG_CLIENT_TOKEN,
    site: DATADOG_SITE,
    service: DATADOG_SERVICE,
    env: DATADOG_ENV,
    version: DATADOG_VERSION,
    sessionSampleRate: 100,
    sessionReplaySampleRate: 20,
    defaultPrivacyLevel: 'mask-user-input',
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    plugins: [reactPlugin({ router: true })],
    // Ignore radar tile image requests to reduce noise in Datadog
    // These are preloaded in bulk and don't need individual tracking
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    beforeSend: (event, _context) => {
      // Filter out resource events for radar tile images
      if (event.type === 'resource' && event.resource) {
        const url = event.resource.url || '';
        // Ignore requests to GCS bucket radar tiles
        if (
          (url.includes('storage.googleapis.com') && url.includes('radar-tiles')) ||
          url.match(/radar-tiles\/\d+\/\w+\/\d{4}-\d{2}-\d{2}/)
        ) {
          return false; // Don't send this event to Datadog
        }
      }
      return true; // Send all other events
    },
  });

  console.log(`[Datadog] RUM initialized for ${DATADOG_SERVICE} (${DATADOG_ENV})`);
}

/**
 * Set user information for the current session
 * Call this after user authentication to enrich session data
 */
export function setDatadogUser(user: {
  id?: string;
  email?: string;
  name?: string;
  role?: string;
}): void {
  if (!DATADOG_ENABLED) return;

  datadogRum.setUser({
    id: user.id,
    email: user.email,
    name: user.name,
    role: user.role,
  });
}

/**
 * Clear user information (call on logout)
 */
export function clearDatadogUser(): void {
  if (!DATADOG_ENABLED) return;

  datadogRum.clearUser();
}

/**
 * Add custom action for tracking specific user interactions
 */
export function trackAction(name: string, context?: Record<string, unknown>): void {
  if (!DATADOG_ENABLED) return;

  datadogRum.addAction(name, context);
}

/**
 * Add custom error to Datadog
 */
export function trackError(error: Error, context?: Record<string, unknown>): void {
  if (!DATADOG_ENABLED) return;

  datadogRum.addError(error, context);
}

export { datadogRum };
